import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initBozpApiClient } from '@/api/bozp-token';
import { installClientErrorReporting } from '@/lib/client-report';
import { router } from '@/router';
import '@/styles/globals.css';

// Wire the shared API client to the in-memory BOZP token before any request fires.
initBozpApiClient();
// Uncaught errors reach the API log with the device's user agent (fixit/4329);
// the ones raised before the handoff exchange wait for its token.
installClientErrorReporting();

// Theme: an explicit `?theme=dark|light` from the opener (the mobile WebView
// passes the app's active scheme) wins; otherwise follow the OS preference,
// live. The stamp drives the `:root[data-theme='dark']` token override.
const applyTheme = (dark: boolean) => {
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
};
const themeParam = new URLSearchParams(window.location.search).get('theme');
if (themeParam === 'dark' || themeParam === 'light') {
  applyTheme(themeParam === 'dark');
} else {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  applyTheme(media.matches);
  media.addEventListener('change', (e) => applyTheme(e.matches));
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
